import { Diagnostic, linter } from "@codemirror/lint";

import {
    LogSearchFieldNames,
    LogSearchFunctionNames,
} from "./schema.js";

const KnownIdentifiers = new Set([
    ...LogSearchFieldNames,
    ...LogSearchFunctionNames,
    "and",
    "or",
    "not",
    "in",
    "eq",
    "ne",
    "gt",
    "ge",
    "lt",
    "le",
]);

function findUnclosedPairs(text: string): Diagnostic[] {
    const diagnostics: Diagnostic[] = [];
    const stack: Array<{ char: string; pos: number }> = [];
    const pairs: Record<string, string> = {
        "(": ")",
        "{": "}",
        "[": "]",
    };
    const closing = new Set(Object.values(pairs));
    let inString = false;

    for (let index = 0; index < text.length; index++) {
        const char = text[index]!;
        const previous = text[index - 1];

        if (char === "\"" && previous !== "\\") {
            inString = !inString;
            continue;
        }

        if (inString)
            continue;

        if (pairs[char]) {
            stack.push({ char, pos: index });
            continue;
        }

        if (closing.has(char)) {
            const last = stack.pop();

            if (!last || pairs[last.char] !== char) {
                diagnostics.push({
                    from: index,
                    to: index + 1,
                    severity: "error",
                    message: `Fermeture inattendue: ${char}`,
                });
            }
        }
    }

    if (inString) {
        diagnostics.push({
            from: Math.max(0, text.lastIndexOf("\"")),
            to: text.length,
            severity: "error",
            message: "Chaîne non fermée.",
        });
    }

    for (const entry of stack) {
        diagnostics.push({
            from: entry.pos,
            to: entry.pos + 1,
            severity: "warning",
            message: `Séquence non fermée: ${entry.char}`,
        });
    }

    return diagnostics;
}

function findSuspiciousIdentifiers(text: string): Diagnostic[] {
    const diagnostics: Diagnostic[] = [];
    const pattern = /\b[a-zA-Z_][a-zA-Z0-9_.]*\b/g;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text))) {
        const token = match[0]!;
        const next = text[match.index + token.length];

        if (KnownIdentifiers.has(token) || token.startsWith("extra."))
            continue;

        if (next === "(") {
            diagnostics.push({
                from: match.index,
                to: match.index + token.length,
                severity: "warning",
                message: `Fonction inconnue: ${token}`,
            });
        }
    }

    return diagnostics;
}

export function getLogSearchDiagnostics(text: string): Diagnostic[] {
    if (!text.trim())
        return [];

    return [
        ...findUnclosedPairs(text),
        ...findSuspiciousIdentifiers(text),
    ];
}

export const logSearchDiagnosticsExtension = linter((view) => getLogSearchDiagnostics(view.state.doc.toString()));
